<template>
  <div>
    <o-description-details
      v-if="hasReservations"
      :term="$t('general.appointments')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ reservations }}</span>
    </o-description-details>

    <o-description-details
      v-if="hasProducts"
      :term="$t('general.products')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ products }}</span>
    </o-description-details>

    <o-description-details
      v-for="tax in order.data.taxTotals"
      :key="tax.tax.idTax"
      :term="tax.tax.name"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ $currency.format(tax.total) }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('general.subtotal')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ subtotal }}</span>
    </o-description-details>

    <o-description-details
      v-if="hasCharges"
      :term="$t('order.charges')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ charges }}</span>
    </o-description-details>

    <o-description-details
      v-if="discounts"
      :term="$t('general.discounts')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ discounts }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('order.tip')"
      align-right
      class="py-1"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <p class="font-medium text-white">
        {{ tip }}
      </p>
    </o-description-details>

    <o-description-details
      :term="$t('general.total')"
      align-right
      class="py-1"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <p class="font-medium text-white">
        {{ total }}
      </p>
    </o-description-details>

    <o-description-details
      :term="$t('general.payments')"
      align-right
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="py-1"
    >
      <span class="font-medium text-white">{{ payments }}</span>
    </o-description-details>

    <o-description-details
      :term="$t('order.remainder')"
      align-right
      class="pt-1"
      term-col="sm:col-span-2"
      definition-col="sm:col-span-4"
      term-class="text-sm leading-5 text-white"
      padding="pt-1"
    >
      <span class="font-medium text-white">{{ remainder }}</span>
    </o-description-details>
  </div>
</template>

<script setup lang="ts">
import {
  ODescriptionDetails,
} from '@/components/elements'

const { $currency } = useNuxtApp()

interface Props {
  order: any
}

const props = defineProps<Props>()

const currencySymbol = computed(() => props.order.data.currencySymbol)
const subtotal = computed(() => $currency.format(props.order.data.totals.subtotal))
const charges = computed(() => $currency.format(props.order.data.totals.charges))
const taxes = computed(() => $currency.format(props.order.data.totals.taxes))
const total = computed(() => $currency.format(props.order.data.totals.total))
const tip = computed(() => $currency.format(Number(props.order.data.totals.tip)))
const payments = computed(() => $currency.format(props.order.data.totals.payments))
const remainder = computed(() => $currency.format(props.order.data.totals.remainder))
const loyalty = computed(() => props.order.data.totals.loyaltyPointsEarned)

const reservations = computed(() => {
  const items = props.order.data.items.reservations
  const total = items.reduce((acc: number, { priceWithIncludedTaxes }: any) => acc + priceWithIncludedTaxes, 0)
  return $currency.format(total)
})

const products = computed(() => {
  const items = props.order.data.items.products
  const total = items.reduce((acc: number, { priceWithIncludedTaxes }: any) => acc + priceWithIncludedTaxes, 0)
  return $currency.format(total)
})

const discounts = computed(() => {
  const d = props.order.data.totals.discounts
  return d ? $currency.format(d) : null
})

const hasCharges = computed(() => props.order.data.items.charges.length)
const hasRemaining = computed(() => props.order.hasRemaining)
const hasLoyalty = computed(() => props.order.data.totals.loyaltyPointsEarned.length)
const hasReservations = computed(() => props.order.data.items.reservations.length)
const hasProducts = computed(() => props.order.data.items.products.length)
const hasDiscounts = computed(() => props.order.data.items.discounts.length)
</script>
